import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import BasicButton from "../../common/components/BasicButton";
import ListPageTopSection from "../../common/components/ListPageTopSection";
import PageWrapper from "../../common/components/PageWrapper";
import SearchBar from "../../common/components/SearchBar";
import Table from "../../common/components/Table";
import {
  useDeleteWelfareContentAsAdminMutation,
  useGetWelfareContentsAsAdminQuery,
  WelfareContentSearchType,
  WelfareContentSortKey,
} from "../../../lib/apollo/graphql/generated";
import { FETCH_SIZE } from "../../../utilities/constants";
import { useForm } from "../../../hooks/useForm";
import { SortDirection } from "global";
import { getTotalPages } from "../../../utilities/pagination";
import ActiveStatus from "../../common/components/ActiveStatus";
import { formatDate } from "../../../utilities/format";
import WelfareCategoriesModal from "../components/WelfareCategoriesModal";
import { useModal } from "../../../hooks/useModal";
import UpdateWelfareModal from "../components/UpdateWelfareModal";

type WelfareFilters = {
  page: number;
  limit: number;
  query: string;
  searchType: WelfareContentSearchType;
  sortKey: WelfareContentSortKey;
  sortDirection: SortDirection;
  welfareCategoryIds: string[];
};

export default function WelfareContainer() {
  const navigation = useNavigate();
  const [welfareId, setWelfareId] = useState("");

  const [
    isCategoriesModalOpen,
    { open: openCategoriesModal, close: closeCategoriesModal },
  ] = useModal();

  const [
    isUpdateModalOpen,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useModal();

  const [filters, onChange, onBatch] = useForm<WelfareFilters>({
    page: 1,
    limit: FETCH_SIZE,
    query: "",
    searchType: WelfareContentSearchType.Title,
    sortKey: WelfareContentSortKey.CreatedAt,
    sortDirection: "DESC",
    welfareCategoryIds: [],
  });

  const { data, loading, refetch } = useGetWelfareContentsAsAdminQuery({
    variables: {
      page: 1,
      limit: FETCH_SIZE,
    },
  });

  const [deleteWelfare, { loading: isDeleting }] =
    useDeleteWelfareContentAsAdminMutation({
      onCompleted: () => {
        alert("복지정보를 비운영 상태로 변경했습니다.");
      },
      refetchQueries: ["GetWelfareContentsAsAdmin"],
    });

  const onDelete = (welfareId: string) => {
    if (isDeleting || !window.confirm("복지정보를 삭제하시겠습니까?")) {
      return;
    }

    deleteWelfare({
      variables: {
        welfareContentId: welfareId,
      },
    });
  };

  const onSearch = () => {
    onChange("page", 1);
    refetch({
      ...filters,
      page: 1,
    });
  };

  const onFiltersApply = (welfareCategoryIds: string[]) => {
    onChange("welfareCategoryIds", welfareCategoryIds);
    refetch({
      ...filters,
      page: 1,
      welfareCategoryIds,
    });
  };

  const onFiltersClear = () => {
    onChange("welfareCategoryIds", []);
    refetch({
      ...filters,
      page: 1,
      welfareCategoryIds: [],
    });
  };

  const onSort = (key?: WelfareContentSortKey, direction?: SortDirection) => {
    const updatedFilters = {
      ...filters,
      sortKey: !key ? WelfareContentSortKey.CreatedAt : key,
      sortDirection: !direction ? "DESC" : (direction as SortDirection),
    };

    onBatch(updatedFilters);

    refetch({
      ...updatedFilters,
    });
  };

  const onUpdate = (id: string) => {
    setWelfareId(id);
    openUpdateModal();
  };

  const isSearchOptionsNotApplied =
    !filters.welfareCategoryIds.length && !filters.query && filters.page === 1;

  const welfares = data?.getWelfareContentsAsAdmin?.edges || [];
  const pageInfo = data?.getWelfareContentsAsAdmin?.pageInfo;
  const totalPages = getTotalPages(pageInfo?.totalCount, FETCH_SIZE);

  return (
    <Fragment>
      <WelfareCategoriesModal
        open={isCategoriesModalOpen}
        onClose={closeCategoriesModal}
        onApply={onFiltersApply}
        onClear={onFiltersClear}
      />
      {!!welfareId && isUpdateModalOpen && (
        <UpdateWelfareModal
          open={isUpdateModalOpen}
          onClose={closeUpdateModal}
          welfareId={welfareId}
        />
      )}
      <PageWrapper>
        <ListPageTopSection>
          <BasicButton
            variant="primary"
            text="카테고리 선택"
            className="flex h-full items-center justify-center"
            onClick={openCategoriesModal}
          />
          <SearchBar
            placeholder="제목을 입력해 주세요"
            value={filters.query}
            onChange={(value) => onChange("query", value)}
            onSearch={onSearch}
            onCancel={
              isSearchOptionsNotApplied
                ? undefined
                : () => {
                    if (isSearchOptionsNotApplied) {
                      return;
                    }

                    const updated = {
                      ...filters,
                      query: "",
                      page: 1,
                      welfareCategoryIds: [],
                    };

                    onBatch(updated);
                    refetch(updated);
                  }
            }
          />
          <BasicButton
            text="복지정보 등록"
            className="ml-auto"
            onClick={() => {
              navigation("/contents/welfares/register");
            }}
          />
        </ListPageTopSection>
        <Table.Wrapper>
          <Table.Header
            sort={{
              key: filters.sortKey,
              direction: filters.sortDirection,
              onSort,
            }}
            tabs={[
              { label: "카테고리" },
              {
                label: "제목",
                className: "w-1/4 flex-shrink-0",
                sortKey: WelfareContentSortKey.Title,
              },
              { label: "설명", className: "w-1/4 flex-shrink-0" },
              {
                label: "앱 등록일자",
                sortKey: WelfareContentSortKey.CreatedAt,
              },
              { label: "상태", sortKey: WelfareContentSortKey.Status },
              { label: "관리" },
            ]}
          />
          <Table.Body isLoading={loading} isEmpty={!welfares?.length}>
            {welfares?.map((welfare) => {
              return (
                <Table.Row
                  onClick={() => {
                    window.open(welfare?.detailPageUrl, "_blank");
                  }}
                >
                  <Table.Column>
                    <div className="flex h-20 items-center overflow-y-auto text-wrap">
                      {welfare?.welfareCategories?.map((category, index) => {
                        const isLast =
                          welfare?.welfareCategories?.length &&
                          index === welfare?.welfareCategories?.length - 1;

                        return (
                          <span key={category.id}>
                            {category.name}
                            {!isLast && ", "}
                          </span>
                        );
                      })}
                    </div>
                  </Table.Column>
                  <Table.Column className="line-clamp-3 flex w-1/4 flex-shrink-0 text-ellipsis text-wrap font-bold">
                    {welfare?.title}
                  </Table.Column>
                  <Table.Column className="line-clamp-3 flex w-1/4 flex-shrink-0 text-ellipsis text-wrap">
                    {welfare?.description}
                  </Table.Column>
                  <Table.Column>
                    {formatDate(welfare?.createdAt, "YY.MM.DD HH:mm")}
                  </Table.Column>
                  <Table.Column>
                    <ActiveStatus status={welfare?.status?.name || ""} />
                  </Table.Column>
                  <Table.RowManageButtons
                    onUpdate={() => onUpdate(welfare.id)}
                    onDelete={
                      welfare?.status?.code === "INACTIVE"
                        ? undefined
                        : () => onDelete(welfare.id)
                    }
                  />
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Wrapper>
        <Table.Pagination
          page={filters.page}
          total={totalPages}
          onPageChange={(newPage) => onChange("page", newPage)}
        />
      </PageWrapper>
    </Fragment>
  );
}
